import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Search, Trash2, Mail, Phone, Building2, Calendar, AlertCircle, FileText, Globe, CheckCircle2, XCircle, TrendingUp, DollarSign } from "lucide-react";
import { EmailTrackingStats } from "@/components/EmailTrackingStats";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface FormSubmission {
  id: string;
  form_type: string;
  name: string;
  email: string;
  phone: string | null;
  company_name: string | null;
  additional_data: any;
  created_at: string;
}

interface AbandonedForm {
  id: string;
  session_id: string;
  form_type: string;
  partial_data: any;
  email: string | null;
  created_at: string;
  updated_at: string;
  recovery_email_sent: boolean;
  recovery_email_sent_at: string | null;
  completed: boolean;
  recovered: boolean;
  email_sequence_stage: number;
  last_sequence_email_sent_at: string | null;
}

interface GrowthPlanLead {
  id: string;
  first_name: string;
  email: string;
  business_name: string | null;
  source: string;
  industry: string | null;
  website_url: string | null;
  current_revenue: string | null;
  target_revenue: string | null;
  stage: string;
  report_type: string;
  website_scraped: boolean;
  scrape_error: string | null;
  created_at: string;
}

interface ReportUnlock {
  id: string;
  email: string;
  amount_paid: number | null;
  currency: string;
  unlock_method: string;
  is_verified: boolean;
  created_at: string;
}

interface StripeTransaction {
  id: string;
  email: string;
  amount: number;
  currency: string;
  status: string;
  created: string;
}

interface StripeData {
  totalRevenue: number;
  totalPayments: number;
  transactions: StripeTransaction[];
  dailyRevenue: { date: string; amount: number }[];
  currency: string;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<FormSubmission[]>([]);
  const [abandonedForms, setAbandonedForms] = useState<AbandonedForm[]>([]);
  const [filteredAbandonedForms, setFilteredAbandonedForms] = useState<AbandonedForm[]>([]);
  const [growthPlanLeads, setGrowthPlanLeads] = useState<GrowthPlanLead[]>([]);
  const [reportUnlocks, setReportUnlocks] = useState<ReportUnlock[]>([]);
  const [stripeData, setStripeData] = useState<StripeData | null>(null);
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [abandonedSearchTerm, setAbandonedSearchTerm] = useState("");
  const [reportSearchTerm, setReportSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingRecovery, setIsSendingRecovery] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchAbandonedForms();
    fetchGrowthPlanLeads();
    fetchReportUnlocks();
    fetchStripeData();

    // Set up real-time subscription for form submissions
    const submissionsChannel = supabase
      .channel('form-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'form_submissions'
        },
        () => {
          fetchSubmissions();
        }
      )
      .subscribe();

    // Set up real-time subscription for abandoned forms
    const abandonedChannel = supabase
      .channel('abandoned-forms-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'abandoned_form_submissions'
        },
        () => {
          fetchAbandonedForms();
        }
      )
      .subscribe();

    // Set up real-time subscription for growth plan leads
    const growthPlanChannel = supabase
      .channel('growth-plan-leads-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'growth_plan_leads'
        },
        () => {
          fetchGrowthPlanLeads();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(submissionsChannel);
      supabase.removeChannel(abandonedChannel);
      supabase.removeChannel(growthPlanChannel);
    };
  }, []);

  useEffect(() => {
    // Filter submissions based on search term
    const filtered = submissions.filter((submission) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        submission.name.toLowerCase().includes(searchLower) ||
        submission.email.toLowerCase().includes(searchLower) ||
        submission.form_type.toLowerCase().includes(searchLower) ||
        (submission.company_name && submission.company_name.toLowerCase().includes(searchLower))
      );
    });
    setFilteredSubmissions(filtered);
  }, [searchTerm, submissions]);

  useEffect(() => {
    // Filter abandoned forms based on search term
    const filtered = abandonedForms.filter((form) => {
      const searchLower = abandonedSearchTerm.toLowerCase();
      const partialName = form.partial_data?.name || '';
      return (
        partialName.toLowerCase().includes(searchLower) ||
        (form.email && form.email.toLowerCase().includes(searchLower)) ||
        form.form_type.toLowerCase().includes(searchLower)
      );
    });
    setFilteredAbandonedForms(filtered);
  }, [abandonedSearchTerm, abandonedForms]);

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from("form_submissions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
      setFilteredSubmissions(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAbandonedForms = async () => {
    try {
      const { data, error } = await supabase
        .from("abandoned_form_submissions")
        .select("*")
        .eq("completed", false)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setAbandonedForms(data || []);
      setFilteredAbandonedForms(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching abandoned forms",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchGrowthPlanLeads = async () => {
    try {
      const { data, error } = await supabase
        .from("growth_plan_leads")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setGrowthPlanLeads(data || []);
    } catch (error: any) {
      console.error("Error fetching growth plan leads:", error.message);
    }
  };

  const fetchReportUnlocks = async () => {
    try {
      const { data, error } = await supabase
        .from("report_unlocks")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReportUnlocks(data || []);
    } catch (error: any) {
      console.error("Error fetching report unlocks:", error.message);
    }
  };

  const fetchStripeData = async () => {
    setIsLoadingStripe(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-payments', {
        body: { action: 'dashboard' }
      });
      
      if (error) throw error;
      if (data?.success) {
        setStripeData(data.data);
      }
    } catch (error: any) {
      console.error("Error fetching Stripe data:", error.message);
    } finally {
      setIsLoadingStripe(false);
    }
  };

  const triggerRecoveryEmails = async () => {
    setIsSendingRecovery(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-recovery-emails');
      
      if (error) throw error;

      toast({
        title: "Recovery Emails Sent",
        description: `Sent ${data.sent} recovery emails. ${data.failed} failed.`,
      });
      
      // Refresh abandoned forms
      fetchAbandonedForms();
    } catch (error: any) {
      toast({
        title: "Error sending recovery emails",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSendingRecovery(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("form_submissions")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Submission deleted successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFormTypeBadgeColor = (formType: string) => {
    const type = formType.toLowerCase();
    if (type.includes("disqualified")) return "bg-amber-100 text-amber-800 border-amber-200";
    if (type.includes("growth_plan") || type.includes("assessment")) return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (type.includes("linkedin_sdr")) return "bg-blue-100 text-blue-800 border-blue-200";
    if (type.includes("enterprise") || type.includes("abm")) return "bg-purple-100 text-purple-800 border-purple-200";
    switch (type) {
      case "hero":
        return "bg-gold/20 text-gold border-gold/30";
      case "survey":
        return "bg-forest/20 text-forest border-forest/30";
      case "cta":
        return "bg-navy/20 text-navy border-navy/30";
      default:
        return "bg-muted text-foreground border-muted";
    }
  };

  const getLeadSource = (submission: FormSubmission): string => {
    const source = submission.additional_data?.source;
    if (source === "fb") return "Facebook";
    if (source === "li" || source === "linkedin") return "LinkedIn";
    if (source === "ig") return "Instagram";
    if (source === "tw" || source === "twitter") return "Twitter/X";
    if (source === "g" || source === "google") return "Google";
    if (source === "direct") return "Direct";
    if (source === "a" || !source) return "Organic";
    return source;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-background to-cream p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-gold to-navy bg-clip-text text-transparent">
                Admin Dashboard
              </CardTitle>
              <CardDescription>
                View and manage all form submissions
              </CardDescription>
            </div>
            <Button variant="outline" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="submissions" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="submissions">
                  Submissions ({submissions.length})
                </TabsTrigger>
                <TabsTrigger value="reports">
                  Reports ({growthPlanLeads.length})
                </TabsTrigger>
                <TabsTrigger value="revenue">
                  💰 Revenue
                </TabsTrigger>
                <TabsTrigger value="abandoned">
                  Abandoned ({abandonedForms.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="submissions">
                <div className="flex items-center space-x-2 mb-6">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, company, or form type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                </div>

                <div className="mb-4 text-sm text-muted-foreground">
                  Showing {filteredSubmissions.length} of {submissions.length} submissions
                </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Email Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        No submissions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSubmissions.map((submission) => (
                      <TableRow key={submission.id}>
                        <TableCell className="font-medium">
                          {submission.name}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              <a
                                href={`mailto:${submission.email}`}
                                className="text-primary hover:underline"
                              >
                                {submission.email}
                              </a>
                            </div>
                            {submission.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="h-3 w-3 text-muted-foreground" />
                                <a
                                  href={`tel:${submission.phone}`}
                                  className="text-primary hover:underline"
                                >
                                  {submission.phone}
                                </a>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {submission.company_name ? (
                            <div className="flex items-center gap-2 text-sm">
                              <Building2 className="h-3 w-3 text-muted-foreground" />
                              {submission.company_name}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`border ${getFormTypeBadgeColor(submission.form_type)}`}>
                            {submission.form_type.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getLeadSource(submission)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <EmailTrackingStats 
                            formSubmissionId={submission.id}
                            recipientEmail={submission.email}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(submission.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this form submission. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(submission.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reports">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Reports</p>
                        <p className="text-2xl font-bold">{growthPlanLeads.length}</p>
                      </div>
                      <FileText className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Full Reports</p>
                        <p className="text-2xl font-bold text-emerald-600">
                          {growthPlanLeads.filter(l => l.report_type === 'full').length}
                        </p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Websites Scraped</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {growthPlanLeads.filter(l => l.website_scraped).length}
                        </p>
                      </div>
                      <Globe className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Paid Unlocks</p>
                        <p className="text-2xl font-bold text-gold">
                          {reportUnlocks.filter(r => r.unlock_method === 'payment' && r.is_verified).length}
                        </p>
                      </div>
                      <DollarSign className="h-8 w-8 text-gold" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Scraping Success Rate */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Website Scraping Success Rate</p>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          <span className="text-sm">Success: {growthPlanLeads.filter(l => l.website_scraped).length}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-sm">Failed/No URL: {growthPlanLeads.filter(l => !l.website_scraped && l.website_url).length}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">No URL: {growthPlanLeads.filter(l => !l.website_url).length}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">
                        {growthPlanLeads.filter(l => l.website_url).length > 0 
                          ? Math.round((growthPlanLeads.filter(l => l.website_scraped).length / growthPlanLeads.filter(l => l.website_url).length) * 100)
                          : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">of URLs provided</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Search */}
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, company, or industry..."
                  value={reportSearchTerm}
                  onChange={(e) => setReportSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>

              {/* Reports Table */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Report Type</TableHead>
                      <TableHead>Website Scraped</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {growthPlanLeads
                      .filter(lead => {
                        const searchLower = reportSearchTerm.toLowerCase();
                        return (
                          lead.first_name?.toLowerCase().includes(searchLower) ||
                          lead.email?.toLowerCase().includes(searchLower) ||
                          lead.business_name?.toLowerCase().includes(searchLower) ||
                          lead.industry?.toLowerCase().includes(searchLower)
                        );
                      })
                      .map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{lead.first_name}</p>
                            <a href={`mailto:${lead.email}`} className="text-sm text-primary hover:underline">
                              {lead.email}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p>{lead.business_name || '-'}</p>
                            {lead.website_url && (
                              <a href={lead.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:underline flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {lead.website_url.replace(/^https?:\/\//, '').slice(0, 30)}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{lead.industry || 'Not specified'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={lead.report_type === 'full' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}>
                            {lead.report_type === 'full' ? '5-Page Full' : 'Basic'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {lead.website_scraped ? (
                            <Badge className="bg-blue-100 text-blue-800">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Scraped
                            </Badge>
                          ) : lead.website_url ? (
                            <Badge variant="outline" className="text-red-600 border-red-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">No URL</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {lead.current_revenue && lead.target_revenue ? (
                              <span>{lead.current_revenue} → {lead.target_revenue}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(lead.created_at)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="revenue">
            <div className="space-y-6">
              {/* Revenue Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-emerald-600 font-medium">Total Revenue (30 days)</p>
                        <p className="text-3xl font-bold text-emerald-700">
                          £{stripeData?.totalRevenue?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="h-12 w-12 bg-emerald-500 rounded-full flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Paid Reports</p>
                        <p className="text-3xl font-bold text-blue-700">
                          {stripeData?.totalPayments || 0}
                        </p>
                      </div>
                      <div className="h-12 w-12 bg-blue-500 rounded-full flex items-center justify-center">
                        <FileText className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-gold/10 to-gold/20 border-gold/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gold font-medium">Avg. per Report</p>
                        <p className="text-3xl font-bold text-gold">
                          £{stripeData?.totalPayments ? (stripeData.totalRevenue / stripeData.totalPayments).toFixed(2) : '0.00'}
                        </p>
                      </div>
                      <div className="h-12 w-12 bg-gold rounded-full flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Refresh Button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={fetchStripeData}
                  disabled={isLoadingStripe}
                >
                  {isLoadingStripe ? "Loading..." : "Refresh Data"}
                </Button>
              </div>

              {/* Daily Revenue */}
              {stripeData?.dailyRevenue && stripeData.dailyRevenue.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Daily Revenue (Last 30 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {stripeData.dailyRevenue.map((day) => (
                        <div key={day.date} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                          <span className="text-sm text-muted-foreground">{new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                          <span className="font-semibold text-emerald-600">£{day.amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Transactions Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Transactions</CardTitle>
                  <CardDescription>Payments from £9 Growth Reports</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingStripe ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
                    </div>
                  ) : stripeData?.transactions && stripeData.transactions.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stripeData.transactions.map((transaction) => (
                            <TableRow key={transaction.id}>
                              <TableCell>
                                <a href={`mailto:${transaction.email}`} className="text-primary hover:underline">
                                  {transaction.email}
                                </a>
                              </TableCell>
                              <TableCell>
                                <span className="font-semibold text-emerald-600">
                                  £{(transaction.amount / 100).toFixed(2)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Paid
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {formatDate(transaction.created)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>No transactions found in the last 30 days</p>
                      <p className="text-sm">Payments from the £9 report will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="abandoned">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center space-x-2 flex-1">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search abandoned forms by name, email, or form type..."
                    value={abandonedSearchTerm}
                    onChange={(e) => setAbandonedSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <Button 
                  onClick={triggerRecoveryEmails} 
                  disabled={isSendingRecovery}
                  variant="secondary"
                >
                  {isSendingRecovery ? "Sending..." : "Process Email Sequences"}
                </Button>
              </div>

              <div className="mb-4 text-sm text-muted-foreground">
                Showing {filteredAbandonedForms.length} of {abandonedForms.length} abandoned forms
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partial Info</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Form Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAbandonedForms.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No abandoned forms found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAbandonedForms.map((form) => {
                        const partialData = form.partial_data || {};
                        const fieldsEntered = Object.keys(partialData).filter(
                          key => partialData[key] !== '' && partialData[key] !== null
                        );

                        return (
                          <TableRow key={form.id}>
                            <TableCell>
                              <div className="space-y-1 text-sm">
                                {partialData.name && (
                                  <div><strong>Name:</strong> {partialData.name}</div>
                                )}
                                {partialData.businessName && (
                                  <div><strong>Business:</strong> {partialData.businessName}</div>
                                )}
                                {partialData.phone && (
                                  <div><strong>Phone:</strong> {partialData.phone}</div>
                                )}
                                {partialData.company && (
                                  <div><strong>Company:</strong> {partialData.company}</div>
                                )}
                                <div className="text-muted-foreground">
                                  {fieldsEntered.length} fields filled
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {form.email ? (
                                <a
                                  href={`mailto:${form.email}`}
                                  className="text-primary hover:underline flex items-center gap-1"
                                >
                                  <Mail className="h-3 w-3" />
                                  {form.email}
                                </a>
                              ) : (
                                <span className="text-muted-foreground text-sm">No email</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={getFormTypeBadgeColor(form.form_type)}>
                                {form.form_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {form.email_sequence_stage > 0 ? (
                                  <div className="space-y-1">
                                    <Badge variant="outline" className="bg-blue-50">
                                      Stage {form.email_sequence_stage}/3
                                    </Badge>
                                    {form.last_sequence_email_sent_at && (
                                      <div className="text-xs text-muted-foreground">
                                        Last: {formatDate(form.last_sequence_email_sent_at)}
                                      </div>
                                    )}
                                  </div>
                                ) : form.email ? (
                                  <Badge variant="outline" className="bg-yellow-50">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Ready for Stage 1
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-gray-50">
                                    No Email
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-muted-foreground">
                                {formatDate(form.updated_at)}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  </div>
</div>
  );
};

export default AdminDashboard;
